<template>
  <div class="customer-info-step pa-4">
    <!-- 顧客紐付けセクション (卓全体に対して任意で複数追加) -->
    <v-card
      flat
      outlined
      class="mb-4"
    >
      <v-card-subtitle class="pa-3 pb-1 text-base font-weight-bold">
        顧客の紐付け（任意）
      </v-card-subtitle>
      <v-card-text class="pt-1">
        <div
          v-if="selectedCustomerIds.length === 0"
          class="text--secondary text-caption mb-2"
        >
          まだ顧客は紐付けられていません
        </div>

        <div
          v-for="id in selectedCustomerIds"
          :key="`customer-card-${id}`"
          class="selected-customer-card mb-2"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-2">
              {{ icons.mdiAccountCircle }}
            </v-icon>
            <span class="font-weight-medium">{{ getCustomerName(id) }}</span>
            <v-spacer />
            <v-btn
              icon
              small
              @click="removeCustomerId(id)"
            >
              <v-icon small>
                {{ icons.mdiClose }}
              </v-icon>
            </v-btn>
          </div>
          <customer-custom-field-list
            :customer="getCustomer(id)"
            :custom-fields="customFields"
          />
        </div>

        <v-btn
          outlined
          large
          color="primary"
          @click="customerDialogVisible = true"
        >
          <v-icon left>
            {{ icons.mdiPlus }}
          </v-icon>
          顧客を選択 / 新規登録
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- 指名 / リファラル サブタブ -->
    <template v-if="showNominationTab || showReferralTab">
      <v-tabs
        v-model="activeTab"
        grow
        class="mb-3"
      >
        <v-tab v-if="showNominationTab">
          指名
        </v-tab>
        <v-tab v-if="showReferralTab">
          リファラル
        </v-tab>
      </v-tabs>

      <v-tabs-items :value="activeTab">
        <!-- 指名 -->
        <v-tab-item v-if="showNominationTab">
          <div class="pa-2">
            <v-text-field
              v-model="castSearch"
              label="キャストの検索"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="mb-3"
            />
            <nomination-radio-row
              v-for="cast in filteredCasts"
              :key="`nrr-${cast.id}`"
              :cast="cast"
              :nomination-defs="nominationDefs"
              :selected-vendible-id="getNominationVendibleIdFor(cast.id)"
              @change="onNominationChange(cast, $event)"
            />
            <div
              v-if="filteredCasts.length === 0"
              class="text-center text--secondary py-4"
            >
              該当するキャストが見つかりません
            </div>
          </div>
        </v-tab-item>

        <!-- リファラル: 紹介者(ユーザー)行ごとにラベル(referral)を1つ選択 -->
        <v-tab-item v-if="showReferralTab">
          <div class="pa-2">
            <v-text-field
              v-model="referralUserSearch"
              label="紹介者の検索"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="mb-3"
            />
            <referral-user-row
              v-for="u in filteredReferralUsers"
              :key="`rur-${u.id}`"
              :user="u"
              :referral-options="referralOptions"
              :selected-referral-id="getReferralIdForUser(u.id)"
              @change="onReferralForUserChange(u, $event)"
            />
            <div
              v-if="filteredReferralUsers.length === 0"
              class="text-center text--secondary py-4"
            >
              該当する紹介者が見つかりません
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </template>

    <div
      v-else
      class="text-center pa-4 text--secondary"
    >
      指名・リファラル設定は不要です（キャストまたはマスタが未登録）
    </div>

    <!-- 顧客選択ダイアログ -->
    <v-dialog
      v-model="customerDialogVisible"
      max-width="600"
    >
      <v-card>
        <v-card-title>顧客を検索 / 新規登録</v-card-title>
        <!-- 検索バー + 新規登録ボタンを上部に sticky 固定 -->
        <div class="customer-dialog-header pa-4 pt-2">
          <div
            class="d-flex align-center"
            style="gap: 8px;"
          >
            <v-text-field
              v-model="customerSearch"
              label="名前で検索"
              outlined
              dense
              hide-details
              autofocus
              class="flex-grow-1"
            />
            <v-btn
              color="primary"
              :disabled="!customerSearch.trim() || isCreating"
              :loading="isCreating"
              @click="handleCreateNewCustomer"
            >
              <v-icon left>
                {{ icons.mdiPlus }}
              </v-icon>
              <template v-if="customerSearch.trim()">
                「{{ customerSearch.trim() }}」で新規登録
              </template>
              <template v-else>
                新規登録
              </template>
            </v-btn>
          </div>
        </div>
        <v-card-text class="pt-0">
          <v-list
            two-line
            class="customer-result-list"
          >
            <v-list-item
              v-for="c in filteredCustomers"
              :key="c.id"
              @click="addCustomerId(c.id)"
            >
              <v-list-item-content>
                <v-list-item-title
                  class="d-flex align-center"
                  style="gap: 8px;"
                >
                  <span class="font-weight-medium">{{ c.name }}</span>
                  <v-chip
                    v-if="c.memberNumber"
                    x-small
                    outlined
                    color="primary"
                  >
                    No.{{ c.memberNumber }}
                  </v-chip>
                  <v-chip
                    v-for="tag in (c.customerTags || []).slice(0, 2)"
                    :key="`tag-${tag.id || tag.name}`"
                    x-small
                    label
                    color="grey lighten-3"
                  >
                    {{ tag.name || tag }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle
                  class="d-flex align-center text-caption"
                  style="gap: 12px;"
                >
                  <span v-if="c.visitCount != null">来店 {{ c.visitCount }}回</span>
                  <span v-if="c.lastVisitedAt">前回 {{ formatDate(c.lastVisitedAt) }}</span>
                  <span v-if="c.birthday">誕生日 {{ formatBirthday(c.birthday) }}</span>
                </v-list-item-subtitle>
                <customer-custom-field-list
                  inline
                  :customer="c.raw || c"
                  :custom-fields="customFields"
                />
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="filteredCustomers.length === 0 && !isSearching"
              disabled
            >
              <v-list-item-content class="text--secondary">
                {{ customerSearch ? '該当する顧客が見つかりません' : '名前を入力してください' }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="isSearching"
              disabled
            >
              <v-list-item-content class="text--secondary">
                検索中…
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="hasMoreCustomers"
              disabled
            >
              <v-list-item-content class="text--secondary text-caption">
                他にも候補があります。名前で絞り込んでください
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="customerDialogVisible = false"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, watch, inject, getCurrentInstance, onMounted,
} from '@vue/composition-api'
import { mdiPlus, mdiClose, mdiAccountCircle } from '@mdi/js'
import CustomerApi from '@/api/v2/admin/customer-management/Customer'
import useCustomerCustomFields from '@/views/v2-temp/common/customer-management/composables/useCustomerCustomFields'
import NominationRadioRow from '../parts/NominationRadioRow.vue'
import ReferralUserRow from '../parts/ReferralUserRow.vue'
import CustomerCustomFieldList from '../parts/CustomerCustomFieldList.vue'

// data:[{id, attributes:{name,...}}] と {id, name,...} の両方を受け取れるように
const flatten = item => {
  if (!item) return null
  if (item.attributes) return { id: item.id, ...item.attributes, raw: item }

  return { ...item, raw: item }
}

export default {
  name: 'CustomerInfoStep',
  components: {
    NominationRadioRow,
    ReferralUserRow,
    CustomerCustomFieldList,
  },
  props: {
    nominations: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    referrals: { type: Array, default: () => [] },
    selectedCustomerIds: { type: Array, default: () => [] },
    castNominations: { type: Array, default: () => [] }, // [{ castId, vendibleId, sellingPrice }]
    referralRowsValue: { type: Array, default: () => [] }, // [{ referralId, userId }]
  },
  emits: [
    'update:selectedCustomerIds',
    'update:castNominations',
    'update:referralRowsValue',
  ],
  setup(props, { emit }) {
    const vm = getCurrentInstance()?.proxy
    const activeTab = ref(0)
    const customerDialogVisible = ref(false)
    const customerSearch = ref('')
    const isCreating = ref(false)
    const INITIAL_DISPLAY_LIMIT = 20
    const SEARCH_DISPLAY_LIMIT = 50
    const castSearch = ref('')
    const referralUserSearch = ref('')

    // カスタムフィールド定義の取得
    const { customFields, getCustomFields } = useCustomerCustomFields(vm || {})
    onMounted(() => {
      try { getCustomFields() } catch (_) { /* noop in tests */ }
    })

    // 既存 OrderCard.vendibleRecipientUsers と同じロジック
    const usersWithTimecard = inject('usersWithTimecard', ref([]))

    const normalizedAllCasts = computed(() => props.users
      .map(flatten)
      .filter(u => u && u.role === 'cast'))

    const normalizedWorkingCasts = computed(() => (usersWithTimecard.value || [])
      .map(flatten)
      .filter(u => u && u.role === 'cast'))

    const normalizedUsers = computed(() => (
      normalizedWorkingCasts.value.length > 0
        ? normalizedWorkingCasts.value
        : normalizedAllCasts.value
    ))

    const filteredCasts = computed(() => {
      const q = (castSearch.value || '').trim().toLowerCase()
      if (!q) return normalizedUsers.value

      return normalizedUsers.value.filter(c => (c.name || '').toLowerCase().includes(q))
    })

    const nominationDefs = computed(() => props.nominations
      .map(flatten)
      .filter(Boolean)
      .map(n => ({
        id: n.id,
        name: n.name || '指名',
        sellingPrice: n.sellingPrice || 0,
      })))

    const referralOptions = computed(() => props.referrals
      .map(flatten)
      .filter(Boolean)
      .map(r => ({ id: r.id, name: r.name || `referral#${r.id}` })))

    // リファラルの紹介者候補は alliance / cast / waiter の3ロール全て
    // ロールごとに「出勤者あり→出勤者、なし→そのロール全員」のフォールバック (旧 ReferralCard と同等)
    const REFERRAL_ROLES = ['alliance', 'cast', 'waiter']
    const referralUsers = computed(() => {
      const allByRole = REFERRAL_ROLES.reduce((acc, role) => {
        acc[role] = props.users.map(flatten).filter(u => u && u.role === role)

        return acc
      }, {})
      const workingByRole = REFERRAL_ROLES.reduce((acc, role) => {
        acc[role] = (usersWithTimecard.value || [])
          .map(flatten).filter(u => u && u.role === role)

        return acc
      }, {})

      return REFERRAL_ROLES.flatMap(role => (
        workingByRole[role].length > 0 ? workingByRole[role] : allByRole[role]
      ))
    })

    const filteredReferralUsers = computed(() => {
      const q = (referralUserSearch.value || '').trim().toLowerCase()
      if (!q) return referralUsers.value

      return referralUsers.value.filter(u => (u.name || '').toLowerCase().includes(q))
    })

    const showNominationTab = computed(() => (
      nominationDefs.value.length > 0 && normalizedUsers.value.length > 0
    ))
    const showReferralTab = computed(() => (
      referralOptions.value.length > 0 && referralUsers.value.length > 0
    ))

    // 検索結果と「選択済み顧客の名前キャッシュ」を統合管理
    const searchResults = ref([])
    const knownCustomers = ref({}) // { [id]: { id, name, raw } }
    const isSearching = ref(false)

    const fetchCustomers = async query => {
      isSearching.value = true
      try {
        const res = await CustomerApi.getCustomers({ q: (query || '').trim() })
        const list = (res?.data?.data || []).map(flatten).filter(Boolean)
        searchResults.value = list
        list.forEach(c => {
          knownCustomers.value = { ...knownCustomers.value, [c.id]: c }
        })
      } catch (_) {
        searchResults.value = []
      } finally {
        isSearching.value = false
      }
    }

    watch(customerDialogVisible, v => {
      if (v) fetchCustomers(customerSearch.value)
    })
    watch(customerSearch, q => {
      if (customerDialogVisible.value) fetchCustomers(q)
    })

    const displayLimit = computed(() => (
      customerSearch.value.trim() ? SEARCH_DISPLAY_LIMIT : INITIAL_DISPLAY_LIMIT
    ))
    const filteredCustomers = computed(() => searchResults.value.slice(0, displayLimit.value))
    const hasMoreCustomers = computed(() => searchResults.value.length > displayLimit.value)

    const getCustomer = id => knownCustomers.value[id] || null
    const getCustomerName = id => {
      const c = knownCustomers.value[id]

      return c?.name || `顧客#${id}`
    }

    const addCustomerId = id => {
      if (props.selectedCustomerIds.includes(id)) return
      emit('update:selectedCustomerIds', [...props.selectedCustomerIds, id])
      customerDialogVisible.value = false
      customerSearch.value = ''
    }
    const removeCustomerId = id => {
      emit('update:selectedCustomerIds', props.selectedCustomerIds.filter(x => x !== id))
    }

    const handleCreateNewCustomer = async () => {
      const name = customerSearch.value.trim()
      if (!name) return
      isCreating.value = true
      try {
        const res = await CustomerApi.createCustomer({ name })
        const created = res?.data?.data
        const flat = flatten(created)
        if (flat) {
          knownCustomers.value = { ...knownCustomers.value, [flat.id]: flat }
          emit('update:selectedCustomerIds', [...props.selectedCustomerIds, flat.id])
          if (vm?.$toast?.success) vm.$toast.success(`顧客「${name}」を作成しました`)
          customerSearch.value = ''
          customerDialogVisible.value = false
        }
      } catch (_) {
        if (vm?.$toast?.error) vm.$toast.error('顧客の作成に失敗しました')
      } finally {
        isCreating.value = false
      }
    }

    // 指名: cast毎に1つの Nomination Vendible を選ぶ
    const getNominationVendibleIdFor = castId => {
      const found = (props.castNominations || []).find(n => n.castId === castId)

      return found?.vendibleId || null
    }
    const onNominationChange = (cast, { vendibleId, def }) => {
      const others = (props.castNominations || []).filter(n => n.castId !== cast.id)
      const next = vendibleId
        ? [...others, { castId: cast.id, vendibleId, sellingPrice: def?.sellingPrice || 0 }]
        : others
      emit('update:castNominations', next)
    }

    // リファラル: ユーザー単位で referralId を1つ保持
    const referralRows = computed(() => props.referralRowsValue || [])
    const getReferralIdForUser = userId => {
      const found = referralRows.value.find(r => r.userId === userId)

      return found?.referralId || null
    }
    const onReferralForUserChange = (user, { referralId }) => {
      const others = referralRows.value.filter(r => r.userId !== user.id)
      const next = referralId
        ? [...others, { referralId, userId: user.id }]
        : others
      emit('update:referralRowsValue', next)
    }

    const formatDate = iso => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      return `${y}/${m}/${day}`
    }

    const formatBirthday = iso => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      return `${m}/${day}`
    }

    return {
      activeTab,
      customerDialogVisible,
      customerSearch,
      isCreating,
      hasMoreCustomers,
      isSearching,
      castSearch,
      referralUserSearch,
      customFields,
      formatDate,
      formatBirthday,
      normalizedUsers,
      filteredCasts,
      nominationDefs,
      referralOptions,
      filteredReferralUsers,
      showNominationTab,
      showReferralTab,
      filteredCustomers,
      getCustomer,
      getCustomerName,
      addCustomerId,
      removeCustomerId,
      handleCreateNewCustomer,
      getNominationVendibleIdFor,
      onNominationChange,
      getReferralIdForUser,
      onReferralForUserChange,
      icons: { mdiPlus, mdiClose, mdiAccountCircle },
    }
  },
}
</script>

<style scoped>
.selected-customer-card {
  border: 1px solid rgba(127, 127, 127, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
}
.customer-dialog-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--v-background-base, #fff);
  border-bottom: 1px solid rgba(127, 127, 127, 0.12);
}
</style>
